import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../../config/api';

type TipoPeriodo = 'mensual' | 'anual';

interface AuditTrailRow {
  usuario?: string | null;
  usuario_sistemas_operativo?: string | null;
  terminal_pc?: string | null;
  fecha_hora?: string | null;
  esquema_objeto?: string | null;
  nombre_objeto?: string | null;
  accion_realizada?: string | null;
  codigo_error?: number | null;
}

interface AuditTrailResponse {
  success: boolean;
  data: AuditTrailRow[];
  total: number;
  page: number;
  per_page: number;
  fecha_inicio: string;
  fecha_fin: string;
}

interface OracleUsersResponse {
  success: boolean;
  usuarios: string[];
}

const PER_PAGE_OPTIONS = [100, 200, 500, 1000];
const MIN_YEAR = 2017;
const EXPORT_TIMEOUT_MS = 600000;
const USERS_LOAD_TIMEOUT_MS = 30000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseDestinatarios = (value: string): string[] => {
  const emails: string[] = [];
  const seen = new Set<string>();
  value.split(/[,;]/).forEach((part) => {
    const email = part.trim().toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    emails.push(email);
  });
  return emails;
};

/** FastAPI espera username=a&username=b, no username[]=a */
const serializeQueryParams = (
  params: Record<string, string | number | string[] | undefined>
): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
    } else {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

const formatApiErrorDetail = (detail: unknown, fallback: string): string => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) =>
        typeof item === 'string' ? item : item?.msg || JSON.stringify(item)
      )
      .join(', ');
  }
  if (typeof detail === 'object' && detail !== null && 'msg' in detail) {
    return String((detail as { msg: string }).msg);
  }
  return fallback;
};

const ReporteDbaAuditTrailUsuarios: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: currentYear - MIN_YEAR + 1 }, (_, i) => MIN_YEAR + i),
    [currentYear]
  );
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(2000, i, 1).toLocaleString('es-ES', { month: 'long' }),
      })),
    []
  );

  const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodo>('mensual');
  const [anio, setAnio] = useState<number>(currentYear);
  const [mes, setMes] = useState<number>(new Date().getMonth() + 1);
  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);
  const [oracleUsers, setOracleUsers] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(200);

  const [rows, setRows] = useState<AuditTrailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailDestinatarios, setEmailDestinatarios] = useState('');
  const [emailFormat, setEmailFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDialogError, setEmailDialogError] = useState('');

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      setError('');
      try {
        const res = await axios.get<OracleUsersResponse>(
          buildApiUrl(API_CONFIG.REPORTES.DBA_AUDIT_TRAIL_USUARIOS),
          { timeout: USERS_LOAD_TIMEOUT_MS }
        );
        setOracleUsers(res.data.usuarios || []);
      } catch (err: any) {
        setOracleUsers([]);
        setError(
          formatApiErrorDetail(
            err.response?.data?.detail,
            'Error al cargar la lista de usuarios Oracle'
          )
        );
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  const filtersValid =
    selectedUsernames.length > 0 &&
    !loadingUsers &&
    anio >= MIN_YEAR &&
    anio <= currentYear &&
    (tipoPeriodo === 'anual' || (mes >= 1 && mes <= 12));

  const buildParams = () => {
    const params: Record<string, string | number | string[]> = {
      username: selectedUsernames,
      tipo_periodo: tipoPeriodo,
      anio,
    };
    if (tipoPeriodo === 'mensual') params.mes = mes;
    return params;
  };

  const exportFilenameLabel = () => {
    if (selectedUsernames.length === 1) return selectedUsernames[0];
    if (selectedUsernames.length === 2) {
      return `${selectedUsernames[0]}_${selectedUsernames[1]}`;
    }
    return `${selectedUsernames[0]}_${selectedUsernames[1]}_multi`;
  };

  const handleUserSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions, (option) => option.value);
    setSelectedUsernames(selected);
  };

  const fetchData = async (targetPage = page, targetPerPage = perPage) => {
    if (!filtersValid) {
      setError('Complete los filtros obligatorios: periodo y al menos un usuario');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const params = {
        ...buildParams(),
        page: targetPage,
        per_page: targetPerPage,
      };
      const res = await axios.get<AuditTrailResponse>(
        buildApiUrl(API_CONFIG.REPORTES.DBA_AUDIT_TRAIL),
        {
          params,
          paramsSerializer: serializeQueryParams,
          timeout: 120000,
        }
      );
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page);
      setPerPage(res.data.per_page);
      setFechaInicio(res.data.fecha_inicio);
      setFechaFin(res.data.fecha_fin);
      setHasSearched(true);
    } catch (err: any) {
      setError(
        formatApiErrorDetail(
          err.response?.data?.detail,
          'Error al consultar el reporte'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConsultar = () => {
    setPage(1);
    fetchData(1, perPage);
  };

  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setPage(1);
    if (hasSearched) fetchData(1, value);
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const downloadExport = async (format: 'csv' | 'xlsx') => {
    if (!hasSearched || !filtersValid) return;
    setExporting(format);
    setError('');
    setSuccessMessage('');
    try {
      const res = await axios.get(
        buildApiUrl(API_CONFIG.REPORTES.DBA_AUDIT_TRAIL_EXPORT),
        {
          params: { ...buildParams(), format },
          paramsSerializer: serializeQueryParams,
          responseType: 'blob',
          timeout: EXPORT_TIMEOUT_MS,
        }
      );
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      link.href = url;
      link.download = `dba_audit_trail_${exportFilenameLabel()}_${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(
        formatApiErrorDetail(
          err.response?.data?.detail,
          `Error al exportar ${format.toUpperCase()}`
        )
      );
    } finally {
      setExporting(null);
    }
  };

  const openEmailDialog = () => {
    setEmailDialogError('');
    setSuccessMessage('');
    setShowEmailDialog(true);
  };

  const closeEmailDialog = () => {
    if (sendingEmail) return;
    setShowEmailDialog(false);
    setEmailDialogError('');
  };

  const validateEmailDestinatarios = (): string[] => {
    const emails = parseDestinatarios(emailDestinatarios);
    if (emails.length === 0) {
      throw new Error('Ingrese al menos un correo destino');
    }
    const invalid = emails.find((email) => !EMAIL_PATTERN.test(email));
    if (invalid) {
      throw new Error(`Correo inválido: ${invalid}`);
    }
    return emails;
  };

  const sendReportByEmail = async () => {
    if (!hasSearched || !filtersValid) return;
    setEmailDialogError('');
    setError('');
    setSuccessMessage('');

    try {
      validateEmailDestinatarios();
    } catch (err: any) {
      setEmailDialogError(err.message || 'Correo inválido');
      return;
    }

    setSendingEmail(true);
    try {
      const res = await axios.post(
        buildApiUrl(API_CONFIG.REPORTES.DBA_AUDIT_TRAIL_SEND_EMAIL),
        {
          destinatarios: emailDestinatarios,
          format: emailFormat,
          ...buildParams(),
        },
        { timeout: EXPORT_TIMEOUT_MS }
      );
      setShowEmailDialog(false);
      setEmailDestinatarios('');
      setSuccessMessage(res.data.message || 'Correo enviado correctamente');
    } catch (err: any) {
      setEmailDialogError(
        formatApiErrorDetail(err.response?.data?.detail, 'Error al enviar el correo')
      );
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Reporte DbaAuditTrail Usuarios
          </h1>
          <p className="mt-1 text-gray-600">
            Consulta de auditoría Oracle `dba_audit_trail` con filtros obligatorios
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo periodo *
              </label>
              <select
                value={tipoPeriodo}
                onChange={(e) => setTipoPeriodo(e.target.value as TipoPeriodo)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="mensual">Mensual</option>
                <option value="anual">Anual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Año *
              </label>
              <select
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            {tipoPeriodo === 'mensual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mes *
                </label>
                <select
                  value={mes}
                  onChange={(e) => setMes(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usuarios Oracle *
              </label>
              {loadingUsers ? (
                <div className="text-sm text-gray-500 py-2">Cargando usuarios...</div>
              ) : (
                <>
                  <select
                    multiple
                    size={8}
                    value={selectedUsernames}
                    onChange={handleUserSelect}
                    disabled={oracleUsers.length === 0}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    {oracleUsers.map((user) => (
                      <option key={user} value={user}>
                        {user}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Seleccione uno o más usuarios (Ctrl+clic)
                  </p>
                  {selectedUsernames.length > 0 && (
                    <p className="mt-1 text-xs text-gray-600">
                      {selectedUsernames.length} usuario(s) seleccionado(s)
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={handleConsultar}
              disabled={!filtersValid || loading}
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Consultando...' : 'Consultar'}
            </button>
            <button
              onClick={() => downloadExport('csv')}
              disabled={!hasSearched || exporting !== null || sendingEmail}
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {exporting === 'csv' ? 'Exportando CSV...' : 'Exportar CSV'}
            </button>
            <button
              onClick={() => downloadExport('xlsx')}
              disabled={!hasSearched || exporting !== null || sendingEmail}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {exporting === 'xlsx' ? 'Exportando Excel...' : 'Exportar Excel'}
            </button>
            <button
              onClick={openEmailDialog}
              disabled={!hasSearched || exporting !== null || sendingEmail}
              className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {sendingEmail ? 'Enviando correo...' : 'Enviar por correo'}
            </button>
          </div>

          {hasSearched && fechaInicio && (
            <p className="mt-3 text-sm text-gray-600">
              Rango aplicado: {fechaInicio} — {fechaFin} | Total registros: {total}
              {selectedUsernames.length > 1 && (
                <> | Usuarios: {selectedUsernames.join(', ')}</>
              )}
            </p>
          )}
        </div>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-gray-600">
              {hasSearched ? `${total} registros encontrados` : 'Sin consulta'}
            </span>
            <div className="flex items-center gap-2 text-sm">
              <label htmlFor="perPage">Filas por página:</label>
              <select
                id="perPage"
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                disabled={!hasSearched || loading}
                className="border border-gray-300 rounded-md px-2 py-1"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-6 text-gray-600">Cargando...</div>
          ) : !hasSearched ? (
            <div className="p-6 text-gray-600">
              Seleccione los filtros y presione Consultar.
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-gray-600">No hay registros para los filtros seleccionados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Usuario</th>
                    <th className="px-3 py-2 text-left">Usuario SO</th>
                    <th className="px-3 py-2 text-left">Terminal</th>
                    <th className="px-3 py-2 text-left">Fecha/Hora</th>
                    <th className="px-3 py-2 text-left">Esquema</th>
                    <th className="px-3 py-2 text-left">Objeto</th>
                    <th className="px-3 py-2 text-left">Acción</th>
                    <th className="px-3 py-2 text-left">Código error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((row, idx) => (
                    <tr key={`${row.fecha_hora}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{row.usuario || '—'}</td>
                      <td className="px-3 py-2">{row.usuario_sistemas_operativo || '—'}</td>
                      <td className="px-3 py-2">{row.terminal_pc || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.fecha_hora || '—'}</td>
                      <td className="px-3 py-2">{row.esquema_objeto || '—'}</td>
                      <td className="px-3 py-2">{row.nombre_objeto || '—'}</td>
                      <td className="px-3 py-2">{row.accion_realizada || '—'}</td>
                      <td className="px-3 py-2">{row.codigo_error ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasSearched && total > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchData(page - 1, perPage)}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => fetchData(page + 1, perPage)}
                  disabled={page >= totalPages || loading}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEmailDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Enviar reporte por correo
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Correos destino *
                </label>
                <input
                  type="text"
                  value={emailDestinatarios}
                  onChange={(e) => setEmailDestinatarios(e.target.value)}
                  placeholder="correo1@empresa.com, correo2@empresa.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={sendingEmail}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Separar múltiples correos con coma
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Formato adjunto *
                </label>
                <select
                  value={emailFormat}
                  onChange={(e) => setEmailFormat(e.target.value as 'csv' | 'xlsx')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={sendingEmail}
                >
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="csv">CSV (.csv)</option>
                </select>
              </div>

              {emailDialogError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                  {emailDialogError}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeEmailDialog}
                disabled={sendingEmail}
                className="px-4 py-2 rounded border border-gray-300 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={sendReportByEmail}
                disabled={sendingEmail}
                className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {sendingEmail ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReporteDbaAuditTrailUsuarios;
